import React, { useState, useRef, useEffect } from 'react';
import { MoreVertical, ShieldOff, UserCog, Crown, LogOut } from 'lucide-react';
import { tabService, type WorkspaceMember, type WorkspaceRole } from '@/services/tabs';
import { ConfirmDialog } from '@/components/ui/Modal';
import { toast } from 'sonner';

interface MemberRowMenuProps {
  workspaceId: string;
  member: WorkspaceMember;
  /** Caller's role in the workspace — controls which actions are visible. */
  callerRole: string;
  /** Whether the caller is the workspace owner. */
  callerIsOwner: boolean;
  /** UUID of the currently logged-in user (for "leave" action). */
  callerUserUuid: string;
  onChanged: () => void;
}

const ROLES: { value: WorkspaceRole; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'editor', label: 'Editor' },
  { value: 'viewer', label: 'Viewer' },
];

function canManage(target: WorkspaceMember, callerRole: string, callerIsOwner: boolean): boolean {
  if (target.role === 'owner') return false; // owners manage themselves via transfer
  if (callerIsOwner) return true;
  if (callerRole !== 'admin') return false;
  // Admins cannot change roles of other admins or the owner.
  if (target.role === 'admin' || target.role === 'owner') return false;
  return true;
}

export const MemberRowMenu: React.FC<MemberRowMenuProps> = ({
  workspaceId,
  member,
  callerRole,
  callerIsOwner,
  callerUserUuid,
  onChanged,
}) => {
  const [open, setOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<null | { kind: 'remove' } | { kind: 'leave' }>(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const isSelf = member.user_uuid === callerUserUuid;
  const manageable = canManage(member, callerRole, callerIsOwner);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleChangeRole = async (role: WorkspaceRole) => {
    setBusy(true);
    try {
      await tabService.updateWorkspaceMemberRole(workspaceId, member.user_uuid, role);
      toast.success(`${member.display_name} is now ${role}`);
      setOpen(false);
      onChanged();
    } catch (err) {
      toast.error((err as Error).message ?? 'Failed to change role');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setBusy(true);
    try {
      await tabService.removeWorkspaceMember(workspaceId, member.user_uuid);
      toast.success(`${member.display_name} removed`);
      setConfirmAction(null);
      onChanged();
    } catch (err) {
      toast.error((err as Error).message ?? 'Failed to remove member');
    } finally {
      setBusy(false);
    }
  };

  const handleLeave = async () => {
    setBusy(true);
    try {
      await tabService.leaveWorkspace(workspaceId);
      toast.success('You left the workspace');
      setConfirmAction(null);
      onChanged();
    } catch (err) {
      toast.error((err as Error).message ?? 'Failed to leave workspace');
    } finally {
      setBusy(false);
    }
  };

  // Owners cannot leave a workspace they own — they have to delete or transfer.
  const canSelfLeave = isSelf && !callerIsOwner && member.role !== 'owner';

  if (!manageable && !canSelfLeave) {
    return null;
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="p-1.5 rounded-md text-khiimori-text-muted hover:text-khiimori-text-primary hover:bg-khiimori-bg-elevated/60 transition-colors"
        title="Member actions"
        aria-label="Member actions"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-20 w-48 rounded-md border border-khiimori-border bg-khiimori-bg-elevated shadow-lg py-1">
          {manageable && (
            <>
              <div className="px-3 py-1 text-xs uppercase tracking-wide text-khiimori-text-muted">
                Change role
              </div>
              {ROLES.filter((r) => r.value !== member.role).map((r) => (
                <button
                  key={r.value}
                  type="button"
                  disabled={busy}
                  onClick={() => void handleChangeRole(r.value)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-khiimori-text-primary hover:bg-khiimori-bg-tertiary disabled:opacity-50"
                >
                  <UserCog className="w-4 h-4" />
                  {r.label}
                </button>
              ))}
              {callerIsOwner && member.role !== 'owner' && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void handleChangeRole('admin')}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-khiimori-text-primary hover:bg-khiimori-bg-tertiary disabled:opacity-50"
                  title="Promote to admin"
                >
                  <Crown className="w-4 h-4" /> Make admin
                </button>
              )}
              <div className="my-1 border-t border-khiimori-border/60" />
              <button
                type="button"
                disabled={busy}
                onClick={() => setConfirmAction({ kind: 'remove' })}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-500 hover:bg-red-500/10 disabled:opacity-50"
              >
                <ShieldOff className="w-4 h-4" />
                Remove from workspace
              </button>
            </>
          )}
          {canSelfLeave && (
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirmAction({ kind: 'leave' })}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-khiimori-text-primary hover:bg-khiimori-bg-tertiary disabled:opacity-50"
            >
              <LogOut className="w-4 h-4" />
              Leave workspace
            </button>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmAction?.kind === 'remove'}
        onClose={() => setConfirmAction(null)}
        onConfirm={() => void handleRemove()}
        title="Remove member"
        message={
          confirmAction?.kind === 'remove'
            ? `Remove ${member.display_name} from the workspace? They will lose access to shared tabs.`
            : ''
        }
        confirmText="Remove"
        variant="danger"
        isLoading={busy}
      />
      <ConfirmDialog
        isOpen={confirmAction?.kind === 'leave'}
        onClose={() => setConfirmAction(null)}
        onConfirm={() => void handleLeave()}
        title="Leave workspace"
        message="Leave this workspace? You will lose access to its shared tabs. The owner can re-invite you later."
        confirmText="Leave"
        variant="warning"
        isLoading={busy}
      />
    </div>
  );
};

export default MemberRowMenu;
